<template>
  <ListComponent title="Bulk Upload Review">
    <template #actions>
      <v-btn color="primary" outlined depressed class="mr-2" @click="getUploadList()">
        <v-icon left small>mdi-refresh</v-icon>
        Refresh
      </v-btn>
      <v-btn
        color="primary"
        outlined
        depressed
        class="mr-2"
        :to="{ name: 'app-admin-vendor-subhire-upload-monitor' }"
      >
        <v-icon left small>mdi-chart-timeline-variant</v-icon>
        Activity Monitor
      </v-btn>
      <v-btn color="primary" depressed @click="showUploadDialog = true">
        <v-icon left small>mdi-tray-arrow-up</v-icon>
        New Upload
      </v-btn>
    </template>

    <template #list>
      <v-row class="ma-0">
        <v-col cols="12" sm="6" md="3" class="pa-2">
          <v-card outlined class="fill-height">
            <v-card-text>
              <div class="d-flex align-center">
                <v-avatar color="amber lighten-5" size="40" class="mr-3">
                  <v-icon color="amber darken-3">mdi-clock-outline</v-icon>
                </v-avatar>
                <span class="overline grey--text">Pending Review</span>
              </div>
              <div class="text-h4 font-weight-bold mt-3">{{ summary.pendingReview }}</div>
              <div class="caption grey--text">Awaiting action</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3" class="pa-2">
          <v-card outlined class="fill-height">
            <v-card-text>
              <div class="d-flex align-center">
                <v-avatar color="blue lighten-5" size="40" class="mr-3">
                  <v-icon color="blue darken-2">mdi-arrow-right</v-icon>
                </v-avatar>
                <span class="overline grey--text">In Process</span>
              </div>
              <div class="text-h4 font-weight-bold mt-3">{{ summary.inProcess }}</div>
              <div class="caption grey--text">Partially reviewed</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3" class="pa-2">
          <v-card outlined class="fill-height">
            <v-card-text>
              <div class="d-flex align-center">
                <v-avatar color="green lighten-5" size="40" class="mr-3">
                  <v-icon color="green darken-2">mdi-check-circle-outline</v-icon>
                </v-avatar>
                <span class="overline grey--text">Completed</span>
              </div>
              <div class="text-h4 font-weight-bold mt-3">{{ summary.completed }}</div>
              <div class="caption grey--text">All jobs resolved</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3" class="pa-2">
          <v-card outlined class="fill-height">
            <v-card-text>
              <div class="d-flex align-center">
                <v-avatar color="blue lighten-5" size="40" class="mr-3">
                  <v-icon color="blue darken-2">mdi-file-multiple-outline</v-icon>
                </v-avatar>
                <span class="overline grey--text">Total Uploads</span>
              </div>
              <div class="text-h4 font-weight-bold mt-3">{{ summary.totalUploads }}</div>
              <div class="caption grey--text">All vendors</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="5" class="pa-2 d-flex align-center">
          <v-text-field
            label="Search uploads, vendors..."
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            @click:clear="onEnter()"
            v-on:keyup.enter="onEnter"
            v-model="filters.search"
          />
        </v-col>
        <v-col cols="6" md="3" class="pa-2">
          <v-select
            :items="statusOptions"
            v-model="filters.status"
            label="All statuses"
            outlined
            dense
            clearable
            hide-details="auto"
            item-text="text"
            item-value="value"
            @change="onEnter"
          />
        </v-col>
        <v-col cols="6" md="4" class="pa-2">
          <v-select
            :items="vendorOptions"
            v-model="filters.vendor"
            label="All vendors"
            outlined
            dense
            clearable
            hide-details="auto"
            item-text="company_title"
            item-value="id"
            @change="onEnter"
          />
        </v-col>

        <v-col cols="12" class="pa-2">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 480px)"
            class="ag-theme-alpine"
            id="adminBulkUploadGrid"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            @columnMoved="resizeGrid"
            @columnVisible="resizeGrid"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :suppressDragLeaveHidesColumns="true"
            :rowData="uploadList"
          ></AgGridVue>
        </v-col>
        <v-col v-if="totalItems > 0" cols="12" class="d-flex justify-end">
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
      </v-row>

    </template>

    <template #dialogs>
      <VendorSubHireUploadDialog
        v-model="showUploadDialog"
        isAdmin
        @refreshList="getUploadList()"
      />
    </template>
  </ListComponent>
</template>

<script>
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import AdminVendorSubHireUploadViewButton from "@/components/AgGridComponents/AdminVendorSubHireUploadViewButton.vue";
import Pagination from "@/components/General/Pagination.vue";
import VendorSubHireUploadDialog from "@/components/ModuleBased/dialogForms/VendorSubHireUploadDialog.vue";

export default {
  components: {
    ListComponent,
    AgGridVue,
    agColumnHeader: CustomHeader,
    AdminVendorSubHireUploadViewButton,
    Pagination,
    VendorSubHireUploadDialog,
  },

  data() {
    return {
      filters: {},
      showUploadDialog: false,

      pageNo: 1,
      itemsPerPage: 20,
      totalItems: 0,

      statusOptions: [
        { text: "Pending", value: "pending" },
        { text: "In Process", value: "in_process" },
        { text: "Completed", value: "completed" },
      ],
      vendorOptions: [],

      summary: {
        pendingReview: 0,
        inProcess: 0,
        completed: 0,
        totalUploads: 0,
      },

      uploadList: [],

      headers: [
        {
          headerName: "Upload ID",
          field: "id",
          sortable: true,
          minWidth: 220,
          tooltipField: "id",
          cellClass: ["primary--text font-weight-bold"],
          valueGetter: (params) => {
            if (!params.data) return "";
            const vendor = params.data.vendor_name || "-";
            const date = params.data.uploaded_on
              ? this.$globals.formatDate(params.data.uploaded_on)
              : "-";
            return `UPL-${params.data.id}-${vendor}-${date}`;
          },
        },
        {
          headerName: "File",
          field: "file_name",
          tooltipField: "file_name",
          sortable: true,
          minWidth: 200,
        },
        {
          headerName: "Vendor",
          field: "vendor_name",
          sortable: true,
          minWidth: 170,
        },
        {
          headerName: "Uploaded On",
          field: "uploaded_on",
          sortable: true,
          minWidth: 160,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Uploaded By",
          field: "uploaded_by",
          sortable: true,
          minWidth: 150,
        },
        {
          headerName: "Draft Jobs",
          field: "total_draft_subhirejobs",
          sortable: true,
          minWidth: 110,
        },
        {
          headerName: "Breakdown",
          field: "job_breakdown",
          minWidth: 240,
          valueGetter: (params) => {
            if (!params.data) return [];
            const breakdown = [];
            if (params.data.pending) {
              breakdown.push({ label: `${params.data.pending} Pending`, status: "Pending" });
            }
            if (params.data.partially_accepted) {
              breakdown.push({
                label: `${params.data.partially_accepted} Partial`,
                status: "Partially Accepted",
              });
            }
            if (params.data.fully_accepted) {
              breakdown.push({
                label: `${params.data.fully_accepted} Accepted`,
                status: "Fully Accepted",
              });
            }
            if (params.data.rejected) {
              breakdown.push({ label: `${params.data.rejected} Rejected`, status: "Rejected" });
            }
            return breakdown;
          },
          cellRenderer: (params) => {
            const chips = (params.value || [])
              .map((item) => {
                const colors = {
                  Pending: "background:#fef3c7;color:#92400e;",
                  "Partially Accepted": "background:#dbeafe;color:#1e40af;",
                  "Fully Accepted": "background:#dcfce7;color:#166534;",
                  Rejected: "background:#fee2e2;color:#991b1b;",
                };
                const style = colors[item.status] || "background:#eee;color:#333;";
                return `<span style="display:inline-block;${style}border-radius:12px;padding:2px 10px;font-size:12px;font-weight:600;margin-right:4px;margin-bottom:2px;">${item.label}</span>`;
              })
              .join("");
            return `<div style="white-space:normal;line-height:1.8;display:flex;align-items:center;flex-wrap:wrap;height:100%;padding:6px 0;">${chips}</div>`;
          },
          autoHeight: true,
        },
        {
          headerName: "Status",
          field: "status",
          sortable: true,
          minWidth: 130,
          cellRenderer: (params) => {
            const labels = { pending: "Pending", in_process: "In Process", completed: "Completed" };
            const colors = {
              pending: "background:#fef3c7;color:#92400e;",
              in_process: "background:#dbeafe;color:#1e40af;",
              completed: "background:#dcfce7;color:#166534;",
            };
            const style = colors[params.value] || "background:#eee;color:#333;";
            const label = labels[params.value] || params.value;
            return `<span style="display:inline-block;${style}border-radius:12px;padding:4px 12px;font-size:12px;font-weight:700;line-height:16px;white-space:nowrap;">${label}</span>`;
          },
        },
        {
          headerName: "Last Updated",
          field: "modified",
          sortable: true,
          minWidth: 160,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Actions",
          field: "actions",
          minWidth: 110,
          pinned: "right",
          cellRendererFramework: "AdminVendorSubHireUploadViewButton",
        },
      ],

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        resizable: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 46,
      },
    };
  },

  computed: {
    context() {
      return { parentComponent: this };
    },
  },

  methods: {
    resizeGrid() {
      if (this.gridOptions && this.gridOptions.api) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    onEnter() {
      this.pageNo = 1;
      this.getUploadList();
    },
    openUploadDetails(row) {
      this.$router.push(`/app/admin/vendor-subhire-upload/details/${row.id}`);
    },
    exportList() {
      this.$bus.$emit("showToastMessage", {
        message: "Export coming soon.",
        color: "info",
      });
    },
    fetchVendorOptions() {
      this.$api.company
        .getCompanySelectList({ company_type: "Transporter", limit: 500 })
        .then((res) => {
          this.vendorOptions = res.data;
        })
        .catch(() => {});
    },
    updateSummary(allUploads) {
      this.summary.totalUploads = allUploads.length;
      this.summary.pendingReview = allUploads.filter((u) => u.status === "pending").length;
      this.summary.inProcess = allUploads.filter((u) => u.status === "in_process").length;
      this.summary.completed = allUploads.filter((u) => u.status === "completed").length;
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getUploadList();
    },
    prevPage() {
      this.pageNo--;
      this.getUploadList();
    },
    nextPage() {
      this.pageNo++;
      this.getUploadList();
    },
    getUploadList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: (this.pageNo - 1) * this.itemsPerPage,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };
      this.$api.vendorSubHireUpload
        .getAdminVendorSubHireUploadList(params)
        .then((res) => {
          this.uploadList = res.data;
          this.totalItems = res.count != null ? res.count : res.data.length;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
      this.$api.vendorSubHireUpload
        .getAdminVendorSubHireUploadList({ ...this.filters, limit: "all" })
        .then((res) => {
          this.updateSummary(res.data);
        })
        .catch(() => {});
    },
  },

  beforeMount() {
    this.fetchVendorOptions();
    this.getUploadList();
  },
};
</script>

<style scoped>
#adminBulkUploadGrid ::v-deep .ag-header {
  background-color: #e3f2fd;
}
#adminBulkUploadGrid ::v-deep .ag-header-cell-text {
  color: #0d47a1;
  font-weight: 700;
}
</style>
